// src/pages/Admin/Views/SystemTools/SystemToolsPage.jsx
import React, { useState } from 'react';
import {
  manualTriggerInvoice,
  whatsappTestPing,
  manageDb,
  getDebugPackages,
  testPushNotification,
  getLeadVisibility,
} from '../../../../services/systemToolsApi';

// The DB start/stop endpoint and the /test/* debug tools are non-prod only
// (the backend refuses them in prod). Hide those sections when pointed at prod.
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api-dev.selyst.com';
const IS_PROD = API_BASE.includes('api-prod');

const extractError = (err) =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  err?.message ||
  'Request failed.';

// Small presentational result panel for a tool section.
const ResultPanel = ({ state }) => {
  if (!state) return null;
  const { status, data, error } = state;
  if (status === 'loading') {
    return <div className="mt-3 text-sm text-gray-500">Working…</div>;
  }
  if (status === 'error') {
    return (
      <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
      <pre className="whitespace-pre-wrap break-words font-mono text-xs">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

const Card = ({ title, description, children }) => (
  <section className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <h2 className="m-0 text-lg font-semibold text-gray-800">{title}</h2>
    {description && <p className="mt-1 mb-4 text-sm text-gray-500">{description}</p>}
    {children}
  </section>
);

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#8A2BE2] focus:outline-none focus:ring-1 focus:ring-[#8A2BE2]';
const btnCls =
  'inline-flex items-center justify-center rounded-md bg-[#8A2BE2] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#7a1fd0] disabled:cursor-not-allowed disabled:opacity-50';
const btnDangerCls =
  'inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50';
const btnNeutralCls =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

const SystemToolsPage = () => {
  // Per-section state so tools don't clobber each other's results.
  const [invoiceId, setInvoiceId] = useState('');
  const [invoiceState, setInvoiceState] = useState(null);

  const [waPhone, setWaPhone] = useState('');
  const [waState, setWaState] = useState(null);

  const [dbState, setDbState] = useState(null);

  const [pkgState, setPkgState] = useState(null);

  const [pushLead, setPushLead] = useState('');
  const [pushSeller, setPushSeller] = useState('');
  const [pushState, setPushState] = useState(null);

  const [visLead, setVisLead] = useState('');
  const [visSeller, setVisSeller] = useState('');
  const [visState, setVisState] = useState(null);

  const run = async (setState, fn) => {
    setState({ status: 'loading' });
    try {
      const res = await fn();
      setState({ status: 'success', data: res?.data ?? 'OK' });
    } catch (err) {
      setState({ status: 'error', error: extractError(err) });
    }
  };

  const onManualInvoice = (e) => {
    e.preventDefault();
    if (!invoiceId) return;
    run(setInvoiceState, () => manualTriggerInvoice(invoiceId));
  };

  const onWhatsappPing = (e) => {
    e.preventDefault();
    run(setWaState, () => whatsappTestPing({ toPhone: waPhone.trim() || undefined }));
  };

  const onManageDb = (action) => {
    const label = action === 'stop' ? 'STOP' : 'START';
    if (!window.confirm(`Are you sure you want to ${label} the (non-production) database?`)) return;
    run(setDbState, () => manageDb(action));
  };

  const onTestPush = (e) => {
    e.preventDefault();
    if (!pushLead || !pushSeller) return;
    run(setPushState, () => testPushNotification(pushLead, pushSeller));
  };

  const onLeadVisibility = (e) => {
    e.preventDefault();
    if (!visLead || !visSeller) return;
    run(setVisState, () => getLeadVisibility(visLead, visSeller));
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="m-0 text-2xl font-bold text-gray-900">System Tools</h1>
        <p className="mt-1 text-sm text-gray-500">
          Admin operational and diagnostic tools. Actions here run against{' '}
          <span className="font-mono">{IS_PROD ? 'PRODUCTION' : 'non-production'}</span>.
        </p>
      </div>

      {/* Manual invoice */}
      <Card
        title="Manual Invoice Trigger"
        description="Queue Zoho invoice generation for a PAID payment that has no invoice yet. The invoice is generated asynchronously by the tasks worker."
      >
        <form onSubmit={onManualInvoice} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Unified Payment ID</label>
            <input
              type="number"
              min="1"
              className={inputCls}
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              placeholder="e.g. 10432"
            />
          </div>
          <button type="submit" className={btnCls} disabled={!invoiceId || invoiceState?.status === 'loading'}>
            Queue Invoice
          </button>
        </form>
        <ResultPanel state={invoiceState} />
      </Card>

      {/* WhatsApp test ping */}
      <Card
        title="WhatsApp Test Ping"
        description="Send a test WhatsApp template message and log it. Leave the number blank to use the default test number."
      >
        <form onSubmit={onWhatsappPing} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Phone (E.164, no +) — optional
            </label>
            <input
              type="text"
              className={inputCls}
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
              placeholder="e.g. 917506647677"
            />
          </div>
          <button type="submit" className={btnCls} disabled={waState?.status === 'loading'}>
            Send Test Ping
          </button>
        </form>
        <ResultPanel state={waState} />
      </Card>

      {/* Database start/stop — non-prod only */}
      {!IS_PROD && (
        <Card
          title="Database (non-production)"
          description="Start or stop the non-production RDS instance to save cost. Disabled entirely against production."
        >
          <div className="flex gap-3">
            <button
              type="button"
              className={btnNeutralCls}
              disabled={dbState?.status === 'loading'}
              onClick={() => onManageDb('start')}
            >
              Start DB
            </button>
            <button
              type="button"
              className={btnDangerCls}
              disabled={dbState?.status === 'loading'}
              onClick={() => onManageDb('stop')}
            >
              Stop DB
            </button>
          </div>
          <ResultPanel state={dbState} />
        </Card>
      )}

      {/* Dev/debug tools — non-prod only (routers excluded in prod) */}
      {!IS_PROD && (
        <Card
          title="Diagnostics (non-production)"
          description="Debug utilities. These endpoints are excluded from the production deployment."
        >
          {/* Installed packages */}
          <div className="mb-5">
            <div className="mb-2 text-sm font-medium text-gray-700">Installed packages</div>
            <button
              type="button"
              className={btnNeutralCls}
              disabled={pkgState?.status === 'loading'}
              onClick={() => run(setPkgState, getDebugPackages)}
            >
              List packages
            </button>
            <ResultPanel state={pkgState} />
          </div>

          {/* Test push */}
          <form onSubmit={onTestPush} className="mb-5 border-t border-gray-100 pt-4">
            <div className="mb-2 text-sm font-medium text-gray-700">Test push notification</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Lead ID</label>
                <input type="number" className={inputCls} value={pushLead} onChange={(e) => setPushLead(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Seller Profile ID</label>
                <input type="number" className={inputCls} value={pushSeller} onChange={(e) => setPushSeller(e.target.value)} />
              </div>
              <button type="submit" className={btnCls} disabled={!pushLead || !pushSeller || pushState?.status === 'loading'}>
                Send
              </button>
            </div>
            <ResultPanel state={pushState} />
          </form>

          {/* Lead visibility debug */}
          <form onSubmit={onLeadVisibility} className="border-t border-gray-100 pt-4">
            <div className="mb-2 text-sm font-medium text-gray-700">Lead visibility debug</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Lead ID</label>
                <input type="number" className={inputCls} value={visLead} onChange={(e) => setVisLead(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Seller Profile ID</label>
                <input type="number" className={inputCls} value={visSeller} onChange={(e) => setVisSeller(e.target.value)} />
              </div>
              <button type="submit" className={btnNeutralCls} disabled={!visLead || !visSeller || visState?.status === 'loading'}>
                Check
              </button>
            </div>
            <ResultPanel state={visState} />
          </form>
        </Card>
      )}
    </div>
  );
};

export default SystemToolsPage;
